'use client';

import React from 'react';
import { X } from 'lucide-react';
import { useFilters } from '@/context/FiltersContext';
import { formatMrr } from '@/utils/formatters';
import { DEFAULT_FILTERS } from '@/types/filters';

const regionNames = new Intl.DisplayNames(['en'], { type: 'region' });
const countryName = (code: string): string => {
  try {
    return regionNames.of(code) ?? code;
  } catch {
    return code;
  }
};

interface ChipProps {
  label: string;
  onRemove: () => void;
}

const Chip: React.FC<ChipProps> = ({ label, onRemove }) => (
  <span className="inline-flex items-center gap-[3px] bg-bg-raised border border-border-mid rounded-sm px-[6px] py-[2px] text-[10px] text-text-secondary leading-none">
    {label}
    <button
      type="button"
      onClick={onRemove}
      aria-label={`Remove ${label} filter`}
      className="text-text-muted hover:text-text-secondary cursor-pointer -mr-[2px] p-[7px]"
    >
      <X size={10} aria-hidden="true" />
    </button>
  </span>
);

export const ActiveFilters: React.FC = () => {
  const { filters, updateFilter } = useFilters();
  const chips: { key: string; label: string; onRemove: () => void }[] = [];

  if (filters.searchQuery) {
    chips.push({
      key: 'search',
      label: `"${filters.searchQuery}"`,
      onRemove: () => updateFilter('searchQuery', ''),
    });
  }

  if (filters.mrrRange[0] !== DEFAULT_FILTERS.mrrRange[0] || filters.mrrRange[1] !== DEFAULT_FILTERS.mrrRange[1]) {
    const lo = filters.mrrRange[0] > 0 ? formatMrr(filters.mrrRange[0]) : '$0';
    const hi = filters.mrrRange[1] === Infinity ? '∞' : formatMrr(filters.mrrRange[1]);
    chips.push({
      key: 'mrr',
      label: `MRR ${lo}–${hi}`,
      onRemove: () => updateFilter('mrrRange', DEFAULT_FILTERS.mrrRange),
    });
  }

  if (filters.growthRange[0] !== DEFAULT_FILTERS.growthRange[0] || filters.growthRange[1] !== DEFAULT_FILTERS.growthRange[1]) {
    const lo = filters.growthRange[0] === -Infinity ? '-∞' : `${filters.growthRange[0]}%`;
    const hi = filters.growthRange[1] === Infinity ? '∞' : `${filters.growthRange[1]}%`;
    chips.push({
      key: 'growth',
      label: `Growth ${lo}–${hi}`,
      onRemove: () => updateFilter('growthRange', DEFAULT_FILTERS.growthRange),
    });
  }

  if (filters.customersRange[0] !== DEFAULT_FILTERS.customersRange[0] || filters.customersRange[1] !== DEFAULT_FILTERS.customersRange[1]) {
    const lo = filters.customersRange[0].toLocaleString();
    const hi = filters.customersRange[1] === Infinity ? '∞' : filters.customersRange[1].toLocaleString();
    chips.push({
      key: 'customers',
      label: `Customers ${lo}–${hi}`,
      onRemove: () => updateFilter('customersRange', DEFAULT_FILTERS.customersRange),
    });
  }

  if (filters.targetAudience !== null) {
    chips.push({
      key: 'audience',
      label: filters.targetAudience,
      onRemove: () => updateFilter('targetAudience', null),
    });
  }

  if (filters.onSale !== null) {
    chips.push({
      key: 'onSale',
      label: 'For sale',
      onRemove: () => updateFilter('onSale', null),
    });
  }

  [...filters.countries].forEach(code => {
    chips.push({
      key: `country-${code}`,
      label: countryName(code),
      onRemove: () => {
        const next = new Set(filters.countries);
        next.delete(code);
        updateFilter('countries', next);
      },
    });
  });

  if (chips.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-[5px]" aria-label="Active filters">
      {chips.map(chip => (
        <Chip key={chip.key} label={chip.label} onRemove={chip.onRemove} />
      ))}
    </div>
  );
};
